import { fetcher } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { Address } from "@certn/cascadia";
import { BasicButton, Loader, Stack, Text } from "@certn/cascadia-react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { StripeSetupIntentForm } from "./StripeSetupIntentForm";
import { fonts, mapGatewayToStripeKey } from "./constants";
import { createRadarSessionId } from "./radarSession";
import { useStripeAppearance } from "./useStripeAppearance";
import { useClientAccount } from "../../hooks/useClientAccount";

interface Props {
  customerAccountId?: string;
  defaultAddress?: Address;
  handleClose?: () => void;
  onCancel?: () => void;
  includeTitle?: boolean;
  initialPaymentProfileCount: number;
}

type SetupIntentResponse =
  components["schemas"]["CreateSetupIntentSuccessResponse"];

async function createSetupIntent(
  customerAccountId: string,
  radarSessionId?: string
): Promise<SetupIntentResponse> {
  const requestBody: components["schemas"]["CreateClientPortalSetupIntentRequest"] =
    radarSessionId ? { radar_session: radarSessionId } : {};
  return fetcher.submit<SetupIntentResponse>(
    `/api/internal/client-portal/customer-accounts/${customerAccountId}/create-setup-intent/`,
    requestBody
  );
}

export function StripeSetupIntent(props: Props) {
  const { client } = useClientAccount();
  const customerAccountId = props.customerAccountId ?? client?.id;
  const { handleClose, onCancel } = props;
  const { t } = useTranslation();
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const appearance = useStripeAppearance();

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripeGateway, setStripeGateway] = useState<string | null>(null);
  const [intentEpoch, setIntentEpoch] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const remountSetupIntent = useCallback((message?: string) => {
    setClientSecret(null);
    setStripeGateway(null);
    setStripePromise(null);
    setLoadError(null);
    setFormError(message ?? null);
    setIntentEpoch((epoch) => epoch + 1);
  }, []);

  const retryLoad = useCallback(() => {
    remountSetupIntent();
  }, [remountSetupIntent]);

  useEffect(() => {
    let cancelled = false;

    async function fetchClientSecret() {
      if (!customerAccountId) return;
      setLoadError(null);
      try {
        // Probe first without Radar so we learn the exact Stripe gateway
        // (including PREPAY / consumer). Minting with the subsidiary default
        // key attaches the session to the wrong account when those gateways
        // are selected (DAT-1731 review).
        const probe = await createSetupIntent(customerAccountId);
        if (cancelled) return;
        const gateway = probe.stripeGateway || "";
        const gatewayKey = gateway ? mapGatewayToStripeKey[gateway] : undefined;
        const radarSessionId = await createRadarSessionId(gatewayKey);
        if (cancelled) return;

        if (radarSessionId) {
          try {
            const response = await createSetupIntent(
              customerAccountId,
              radarSessionId
            );
            if (cancelled) return;
            setClientSecret(response.clientSecret || "");
            setStripeGateway(response.stripeGateway || gateway);
          } catch (radarCreateError) {
            // Fall back to the probe SetupIntent so card setup still works
            // when the Radar-enriched recreate fails (DAT-1731 Bugbot).
            console.error(radarCreateError);
            if (cancelled) return;
            setClientSecret(probe.clientSecret || "");
            setStripeGateway(gateway);
          }
        } else {
          setClientSecret(probe.clientSecret || "");
          setStripeGateway(gateway);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setLoadError(t("Something went wrong"));
        }
      }
    }
    fetchClientSecret();
    return () => {
      cancelled = true;
    };
    // Omit `t` — identity changes on language switch would remint the SetupIntent
    // and remount Elements mid-entry (DAT-1731 Bugbot).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [customerAccountId, intentEpoch]);

  useEffect(() => {
    if (!stripeGateway) return;
    const stripeKey = mapGatewayToStripeKey[stripeGateway];
    if (!stripeKey) {
      console.error(`No Stripe key configured for gateway: ${stripeGateway}`);
      return;
    }
    setStripePromise(loadStripe(stripeKey));
  }, [stripeGateway]);

  if (loadError) {
    return (
      <Stack gap="space-3">
        <Text style="form/body-secondary">{loadError}</Text>
        <BasicButton type="button" size="medium" onPress={retryLoad}>
          {t("Try again")}
        </BasicButton>
      </Stack>
    );
  }

  if (!clientSecret || !stripePromise) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <Elements
      key={clientSecret}
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance,
        fonts,
      }}
    >
      <StripeSetupIntentForm
        customerAccountId={customerAccountId}
        defaultAddress={props.defaultAddress}
        handleClose={handleClose}
        onCancel={onCancel}
        includeTitle={props.includeTitle}
        initialPaymentProfileCount={props.initialPaymentProfileCount}
        onSetupIntentConsumed={remountSetupIntent}
        initialErrorMessage={formError}
        clientSecret={clientSecret}
      />
    </Elements>
  );
}
